import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, ChevronDown } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";
import { useContent } from "../content/ContentContext";
import { WatchModal } from "./WatchModal";

const EASE = [0.22, 1, 0.36, 1];

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  id: i,
  left: `${(i * 61) % 100}%`,
  top: `${(i * 37 + 12) % 100}%`,
  size: 2 + (i % 3),
  duration: 14 + (i % 5) * 4,
  delay: (i % 7) * 1.3,
}));

export const Hero = () => {
  const ref = useRef(null);
  const [watching, setWatching] = useState(null);
  const { LATEST_EPISODE, SITE } = useContent();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: imgY }}>
        <motion.img
          src={SITE.hero.bg}
          alt="The family of WE NEED TO TALK in a warm, mysterious home"
          className="w-full h-full object-cover img-cinematic"
          initial={{ scale: 1.06 }}
          animate={{ scale: 1.18 }}
          transition={{ duration: 26, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          data-testid="hero-background-image"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/45 to-ink" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-ink/40" />

      {PARTICLES.map((p) => (
        <motion.span
          key={p.id}
          className="absolute rounded-full bg-gold-light/60"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -46, 0], opacity: [0.05, 0.5, 0.05] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "easeInOut" }}
        />
      ))}

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex items-center">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            className="overline-label mb-6"
            data-testid="hero-overline"
          >
            {SITE.hero.overline}
          </motion.p>

          <motion.h1
            className="font-display leading-[0.95] text-5xl sm:text-6xl lg:text-[5.4rem] tracking-tight"
            initial="hidden"
            animate="visible"
            variants={{ visible: { transition: { staggerChildren: 0.16, delayChildren: 0.55 } } }}
            data-testid="hero-headline"
          >
            {["WE NEED", "TO TALK"].map((line) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line === "TO TALK" ? "italic text-gold-light" : "text-ivory"}`}
                  variants={{ hidden: { y: "112%" }, visible: { y: "0%", transition: { duration: 1.15, ease: EASE } } }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.5, ease: EASE }}
            className="font-quote italic text-xl md:text-2xl text-ivory/85 mt-7"
            data-testid="hero-subheadline"
          >
            {SITE.hero.subheadline}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.7, ease: EASE }}
            className="text-sm md:text-base text-smoke max-w-md mt-4 leading-relaxed"
            data-testid="hero-supporting-text"
          >
            {SITE.hero.supporting}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.9, ease: EASE }}
            className="flex flex-wrap gap-4 mt-10"
          >
            <motion.button
              onClick={() => setWatching(LATEST_EPISODE)}
              animate={{
                boxShadow: [
                  "0 0 22px rgba(212,169,60,0.22), 0 0 0px rgba(212,169,60,0)",
                  "0 0 42px rgba(212,169,60,0.5), 0 0 90px rgba(212,169,60,0.18)",
                  "0 0 22px rgba(212,169,60,0.22), 0 0 0px rgba(212,169,60,0)",
                ],
              }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
              className="group inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-ink text-[11px] md:text-xs font-semibold tracking-[0.24em] uppercase px-7 md:px-9 py-4 rounded-full transition-colors duration-300"
              data-testid="hero-teaser-button"
            >
              <Play size={13} className="fill-current" />
              {LATEST_EPISODE.videoId
                ? `Watch episode ${String(LATEST_EPISODE.num).padStart(2, "0")} now`
                : "Watch the official teaser"}
            </motion.button>
            <button
              onClick={() => scrollToId("#family")}
              className="inline-flex items-center gap-3 border border-ivory/25 hover:border-gold/70 hover:text-gold-light text-ivory text-[11px] md:text-xs tracking-[0.24em] uppercase px-7 md:px-9 py-4 rounded-full transition-colors duration-300"
              data-testid="hero-family-button"
            >
              {SITE.hero.familyLabel}
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 2.2 }}
            className="mt-5 text-[10px] tracking-[0.28em] uppercase text-smoke"
            data-testid="hero-premiere-line"
          >
            <span className="text-gold-light">Episode {String(LATEST_EPISODE.num).padStart(2, "0")}</span>{" "}
            {LATEST_EPISODE.videoId ? "— now streaming" : "premieres Monday, 17 August — 12 PM"}
          </motion.p>
        </div>
      </motion.div>

      <motion.button
        onClick={() => scrollToId("#sound")}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-smoke hover:text-gold-light transition-colors duration-300"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
        aria-label="Scroll down"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown size={22} />
      </motion.button>

      <WatchModal episode={watching} open={!!watching} onOpenChange={(o) => !o && setWatching(null)} onSelectEpisode={setWatching} />
    </section>
  );
};
